/**
 * Language-independent deity classification.
 *
 * The catalog ships every name twice: `normalizedName` (ASCII) and `name` (the
 * script of the requested `?l=`). `nodeName()` deliberately returns the second
 * one for every language except English, because that is what belongs ON SCREEN.
 *
 * Anything that DECIDES something from a deity — which Bhajan Babies animation
 * to play, whether a value is a placeholder rather than a real deity — must
 * therefore never read the display name: in Telugu it is 'హనుమాన్', in Kannada
 * 'ಹನುಮಾನ್', and every comparison against an English word silently fails. That
 * is exactly how the Bhajan Babies card came to vanish in all nine non-English
 * languages while looking fine in English.
 *
 * So classification keys off the stable node id first (the catalog's deity
 * nodes are a fixed, small set — see DeityNodeIds) and falls back to the ASCII
 * name only for ids this map predates. `src/utils/visibleNodes.ts` applies the
 * same rule to its "zzz" placeholder check.
 */
import { type AnyGraphNode, nodeName } from '../types/graphNodes';
import { DeityNodeIds } from './deityIcon';

/** Lottie files under public/lottie/babies/ — one per animated deity. */
export type BabiesAnimationFile =
  | 'datta.json'
  | 'devi.json'
  | 'ganapathy.json'
  | 'hanuman.json'
  | 'krishna.json'
  | 'shiva.json'
  | 'skanda.json';

/**
 * The deity name to CLASSIFY by — always ASCII, whatever is on screen.
 * Pair it with the node id; never pass a rendered name to the helpers below.
 */
export function deityAsciiName(node: AnyGraphNode | null | undefined): string | undefined {
  return nodeName(node ?? undefined, 'en');
}

/** Lowercase + strip combining marks, so 'śiva' and 'kṛṣṇa' compare as ASCII. */
function normalize(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();
}

/**
 * Deity node id -> animation. A direct mirror of jnana-native's
 * `getAnimationName` (src/bmScreens/bhajan/view/bhajanBabies/lottieAssets.ts);
 * the two must agree about which deity gets which baby.
 */
const ANIMATION_BY_DEITY_ID: Partial<Record<DeityNodeIds, BabiesAnimationFile>> = {
  [DeityNodeIds.Devi]: 'devi.json',
  [DeityNodeIds.Vishnu]: 'krishna.json',
  [DeityNodeIds.Hanuman]: 'hanuman.json',
  [DeityNodeIds.Guru]: 'datta.json',
  [DeityNodeIds.Datta]: 'datta.json',
  [DeityNodeIds.Ganapathy]: 'ganapathy.json',
  [DeityNodeIds.Murugan]: 'skanda.json',
  [DeityNodeIds.Shiva]: 'shiva.json',
};

/**
 * ASCII-name fallback for a deity id the map above predates. Sanskrit and
 * regional variants, matched as diacritic-insensitive substrings.
 */
const ANIMATION_BY_NAME: Array<[string[], BabiesAnimationFile]> = [
  [['ganapathy', 'ganapati', 'ganesha', 'ganesh', 'vinayaka', 'vighneshvara', 'vighnesvara'], 'ganapathy.json'],
  [['krishna', 'krsna', 'krisna', 'vishnu', 'visnu', 'rama', 'narayana', 'narayan', 'venkateswara', 'venkatesvara', 'hari'], 'krishna.json'],
  [['shiva', 'siva', 'shiv', 'mahadeva', 'mahesvara', 'maheshwara', 'lingaraja', 'natraja', 'nataraja', 'pasupati', 'pashupati'], 'shiva.json'],
  [['skanda', 'murugan', 'murukan', 'subrahmanya', 'subramanya', 'kartikeya', 'karttikeya'], 'skanda.json'],
  [['hanuman', 'anjaneya', 'maruti', 'maaruti'], 'hanuman.json'],
  [['datta', 'dattatreya', 'guru', 'swamiji', 'svamiji', 'sadguru', 'sadhguru'], 'datta.json'],
  [['devi', 'durga', 'lakshmi', 'laksmi', 'saraswati', 'sarasvati', 'parvati', 'ambika', 'shakti', 'sakti', 'kali', 'mata', 'kanyakumari', 'gauri', 'rajarajeshvari', 'rajarajesvari'], 'devi.json'],
];

/**
 * The Bhajan Babies animation for a deity, or null when there is none.
 *
 * Null is NOT a reason to hide the card — a bhajan with a Bhajan Babies video
 * and a deity outside the animated set (ugadi, tattva, "other", …) still has a
 * video to watch. jnana-native draws a static deity image in that case; so does
 * BhajanBaby.
 */
export function babiesAnimationFile(
  deityId?: number | null,
  asciiName?: string | null
): BabiesAnimationFile | null {
  const byId = deityId != null ? ANIMATION_BY_DEITY_ID[deityId as DeityNodeIds] : undefined;
  if (byId) return byId;
  if (!asciiName) return null;
  const lower = normalize(asciiName);
  for (const [keys, file] of ANIMATION_BY_NAME) {
    if (keys.some((k) => lower.includes(k))) return file;
  }
  return null;
}

/**
 * Total times a Bhajan Babies composition plays before stopping.
 *
 * This mirrors jnana-native's `getNumberOfPlays` in
 * `bhajanBabies/allDeities.tsx`. Keep this coupled to the animation selection:
 * the web player has to count completions itself because lottie-web's numeric
 * `loop` option is a repeat count, not a total-play count.
 */
export function babiesAnimationPlayCount(
  deityId?: number | null,
  asciiName?: string | null
): number | null {
  const file = babiesAnimationFile(deityId, asciiName);
  switch (file) {
    case 'devi.json':
      return 1;
    case 'krishna.json':
    case 'hanuman.json':
    case 'ganapathy.json':
      return 2;
    case 'skanda.json':
      return 3;
    case 'shiva.json':
      return 12;
    case 'datta.json':
      return 30;
    default:
      return null;
  }
}

/**
 * Deity values that are really bhajan-type sub-classes, not deities. Showing
 * "Deity: Stotram" as a bhajan's deity is confusing, so the detail view-model
 * and every card drop the value entirely.
 *
 * NOT applied to /browse/deity, which still lists both as browsable
 * categories (deriveFacetValues does not consult this). That is deliberate for
 * now — they are the only entry point to those 74 bhajans — but it means the
 * words do still reach the reader, in English as much as anywhere else.
 */
const PLACEHOLDER_DEITY_IDS = new Set<number>([DeityNodeIds.Stotra, DeityNodeIds.Mantram]);

/**
 * Exact matches only — 'stotramanjari' is a real deity name. 'mantram' is the
 * spelling the catalog actually stores (node 105100); the name-only check that
 * preceded this listed just 'mantra', so that row leaked even in English.
 */
const PLACEHOLDER_DEITY_NAMES = new Set(['stotram', 'stotra', 'mantram', 'mantra']);

export function isPlaceholderDeity(deityId?: number | null, asciiName?: string | null): boolean {
  if (deityId != null && PLACEHOLDER_DEITY_IDS.has(deityId)) return true;
  return !!asciiName && PLACEHOLDER_DEITY_NAMES.has(normalize(asciiName));
}
